import { Disclosure } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { optionsFor, activeRangeId, rangePatch, CUSTOM } from '../lib/dateRange.js'
import { urlForFilters } from '../lib/filterUrl.js'
import { INPUT_BLOCK } from '../lib/ui.js'


function activeFilterCount(value, compact) {
  const keys = compact
    ? ['speakerId', 'tag', 'from', 'to']
    : ['speakerId', 'tag', 'country', 'region', 'city', 'from', 'to']
  return keys.reduce((n, k) => n + (value[k] ? 1 : 0), 0)
}

// #334: the "see the past" control. It writes into the same `from` / `to` the
// From / To fields own rather than introducing a parallel notion of a range —
// so the two can never disagree, and Reset clears both at once. When the
// fields hold something no preset describes, the control says `Custom` instead
// of snapping back to a range nobody chose.
//
// #338: the options are real links now, not buttons. `All of 2026` has a URL —
// `/2026` — so it should be copyable, middle-clickable and crawlable like any
// other address on the site, and the back button should step through range
// changes. `basePath` keeps the map's options pointing at `/map/...`.
export function RangePicker({ value, now = new Date(), basePath = '' }) {
  const options = optionsFor(value, now)
  const active = activeRangeId(value, now)
  const chip = (selected) =>
    `inline-flex min-h-[36px] items-center rounded-full px-3 py-1 text-sm transition ${
      selected
        ? 'bg-brand-600 text-white shadow-sm'
        : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
    }`
  return (
    <div className="-mx-1 overflow-x-auto px-1 scroll-fade-right">
      <div className="flex w-max items-center gap-1.5 pb-1" role="group" aria-label="Date range">
        {options.map((o) => {
          const selected = active === o.id
          const next = { ...value, ...rangePatch(o.id, now) }
          return (
            <Link
              key={o.id}
              to={urlForFilters(basePath, next)}
              aria-current={selected ? 'page' : undefined}
              aria-pressed={selected}
              className={chip(selected)}
            >
              {o.label}
            </Link>
          )
        })}
        {active === CUSTOM ? (
          <span className={chip(true)}>Custom</span>
        ) : null}
      </div>
    </div>
  )
}

function Fields({ value, set, speakers, compact }) {
  const cols = compact
    ? 'grid-cols-1 sm:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  return (
    <div className={`grid gap-3 ${cols}`}>
      <label className="text-sm">
        <span className="block font-medium text-slate-700">Speaker</span>
        <select
          value={value.speakerId || ''}
          onChange={(e) => set({ speakerId: e.target.value })}
          className={INPUT_BLOCK}
        >
          <option value="">All speakers</option>
          {(speakers || []).map((s) => (
            <option key={s.userId} value={s.userId}>
              {s.displayName || s.email || `User #${s.userId}`}
            </option>
          ))}
        </select>
      </label>

      {compact ? null : (
        <>
          <label className="text-sm">
            <span className="block font-medium text-slate-700">Country</span>
            <input
              type="text"
              value={value.country || ''}
              onChange={(e) => set({ country: e.target.value })}
              placeholder="e.g. US"
              className={INPUT_BLOCK}
            />
          </label>

          <label className="text-sm">
            <span className="block font-medium text-slate-700">Region</span>
            <input
              type="text"
              value={value.region || ''}
              onChange={(e) => set({ region: e.target.value })}
              placeholder="e.g. CA"
              className={INPUT_BLOCK}
            />
          </label>

          <label className="text-sm">
            <span className="block font-medium text-slate-700">City</span>
            <input
              type="text"
              value={value.city || ''}
              onChange={(e) => set({ city: e.target.value })}
              placeholder="e.g. San Francisco"
              className={INPUT_BLOCK}
            />
          </label>
        </>
      )}

      <label className="text-sm">
        <span className="block font-medium text-slate-700">From</span>
        <input
          type="date"
          value={value.from || ''}
          onChange={(e) => set({ from: e.target.value })}
          className={INPUT_BLOCK}
        />
      </label>

      <label className="text-sm">
        <span className="block font-medium text-slate-700">To</span>
        <input
          type="date"
          value={value.to || ''}
          onChange={(e) => set({ to: e.target.value })}
          className={INPUT_BLOCK}
        />
      </label>
    </div>
  )
}

export function TagChips({ tags, value, basePath = '' }) {
  const visibleTags = (tags || []).filter((t) => t.usageCount !== 0)
  if (!visibleTags.length) return null
  return (
    <div className="-mx-1 mt-1 overflow-x-auto px-1 scroll-fade-right">
      <ul className="flex w-max gap-1.5 pb-1">
        {visibleTags.map((t) => {
          const active = value.tag === t.slug
          // #338: a tag chip is a filter, so it has a URL. Toggling off means
          // linking to the same view without the tag.
          const href = urlForFilters(basePath, { ...value, tag: active ? '' : t.slug })
          return (
            <li key={t.slug}>
              <Link
                to={href}
                aria-pressed={active}
                className={`inline-flex min-h-[36px] items-center rounded-full px-3 py-1 text-sm transition ${
                  active
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
                }`}
              >
                {t.name || t.slug}
                {t.usageCount != null ? (
                  <span className={`ml-1.5 text-xs ${active ? 'opacity-80' : 'text-slate-400'}`}>
                    {t.usageCount}
                  </span>
                ) : null}
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default function Filters({ value, onChange, speakers, tags, compact = false, basePath = '' }) {
  const set = (patch) => onChange({ ...value, ...patch })
  const count = activeFilterCount(value, compact)

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/60 sm:p-5">
      {/* Outside the mobile Disclosure on purpose: seeing past events is the
          point of the control, and it is worthless behind a collapsed panel. */}
      <div className="mb-3">
        <RangePicker value={value} basePath={basePath} />
      </div>

      {/* Mobile: collapsed by default */}
      <div className="md:hidden">
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full min-h-[44px] items-center justify-between rounded-lg px-1 text-left text-sm font-medium text-slate-700">
                <span className="inline-flex items-center gap-2">
                  <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                  </svg>
                  Filters
                  {count > 0 ? (
                    <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-800">
                      {count}
                    </span>
                  ) : null}
                </span>
                <svg
                  className={`h-4 w-4 text-slate-500 transition ${open ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </Disclosure.Button>
              <Disclosure.Panel className="mt-3 space-y-3">
                <Fields value={value} set={set} speakers={speakers} compact={compact} />
                {count > 0 ? (
                  <button
                    type="button"
                    onClick={() => onChange({})}
                    className="min-h-[36px] rounded-full px-3 py-1 text-sm text-slate-600 hover:bg-slate-100"
                  >
                    Reset filters
                  </button>
                ) : null}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>

      {/* Desktop: always inline */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700">Filters</div>
          {count > 0 ? (
            <button
              type="button"
              onClick={() => onChange({})}
              className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
            >
              Reset
            </button>
          ) : null}
        </div>
        <div className="mt-3">
          <Fields value={value} set={set} speakers={speakers} compact={compact} />
        </div>
      </div>

      <TagChips tags={tags} value={value} basePath={basePath} />
    </section>
  )
}
